import { Component } from 'react';

type Point = {
  x: number;
  y: number;
};

type CustomBrush = {
  image: any;
  width: number;
  height: number;
};

type CustomCheckZone = {
  x: number;
  y: number;
  width: number;
  height: number;
};

interface Props {
  width: number;
  height: number;
  image: any;
  finishPercent?: number;
  triggerPercent?: number;
  onComplete?: () => void;
  onTrigger?: () => void;
  brushSize?: number;
  fadeOutOnComplete?: boolean;
  children?: any;
  customBrush?: CustomBrush;
  customCheckZone?: CustomCheckZone;
  isMobileDevice: boolean;
}

interface State {
  loaded: boolean;
  finished: boolean;
}

class Scratch extends Component<Props, State> {
  isDrawing = false;

  lastPoint: Point | null = null;

  ctx!: CanvasRenderingContext2D;

  canvas!: HTMLCanvasElement;

  brushImage?: any;

  image!: HTMLImageElement;

  constructor(props: Props) {
    super(props);
    this.state = { loaded: false, finished: false };
  }
  onTouchStart: EventListener = (e) => {
    e.preventDefault();
  };

  onTouchMove: EventListener = (e) => {
    e.preventDefault();
  };

  componentDidMount() {
    this.isDrawing = false;
    this.lastPoint = null;
    this.ctx = this.canvas.getContext('2d') as CanvasRenderingContext2D;

    this.canvas.addEventListener('touchstart', this.onTouchStart, false);
    this.canvas.addEventListener('touchmove', this.onTouchMove, false);

    this.image = new Image();
    this.image.crossOrigin = 'Anonymous';
    this.image.onload = () => {
      this.ctx.drawImage(this.image, 0, 0, this.props.width, this.props.height);
      this.setState({ loaded: true });
    };

    this.image.src = this.props.image.src;

    if (this.props.customBrush) {
      this.brushImage = new Image(
        this.props.customBrush.width,
        this.props.customBrush.height
      );
      this.brushImage.src = this.props.customBrush.image.src;
    }
  }

  componentWillUnmount() {
    this.canvas.removeEventListener('touchstart', this.onTouchStart, false);
    this.canvas.removeEventListener('touchmove', this.onTouchMove, false);
  }

  reset = () => {
    this.canvas.style.opacity = '1';
    this.ctx.globalCompositeOperation = 'source-over';
    this.ctx.drawImage(this.image, 0, 0, this.props.width, this.props.height);
  };

  getFilledInPixels(stride: number) {
    if (!(this.canvas && this.ctx)) return 0;

    if (!stride || stride < 1) {
      stride = 1;
    }

    let x = 0;
    let y = 0;
    let width = this.canvas.width;
    let height = this.canvas.height;

    if (this.props.customCheckZone) {
      x = this.props.customCheckZone.x;
      y = this.props.customCheckZone.y;
      width = this.props.customCheckZone.width;
      height = this.props.customCheckZone.height;
    }

    const pixels = this.ctx.getImageData(x, y, width, height);

    const total = pixels.data.length / stride;
    let count = 0;

    // for (let i = 0; i < pixels.data.length; i += stride) {
    //   // @ts-ignore
    //   if (parseInt(pixels.data[i], 10) === 0) {
    //     count++;
    //   }
    // }

    for (let i = 3; i < pixels.data.length; i += stride) {
      if (pixels.data[i] === 0) {
        count++;
      }
    }

    return Math.round((count / total) * 100);
  }

  getMouse(e: any, canvas: HTMLCanvasElement) {
    if (!canvas) return { x: 0, y: 0 };

    const { top, left } = canvas.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft =
      window.pageXOffset || document.documentElement.scrollLeft;

    let x = 0;
    let y = 0;

    if (e && e.pageX && e.pageY) {
      x = e.pageX - left - scrollLeft;
      y = e.pageY - top - scrollTop;
    } else if (e && e.touches) {
      x = e.touches[0].clientX - left - scrollLeft;
      y = e.touches[0].clientY - top - scrollTop;
    }

    return { x, y };
  }

  distanceBetween(point1: Point | null, point2: Point | null) {
    if (point1 && point2) {
      return Math.sqrt((point2.x - point1.x) ** 2 + (point2.y - point1.y) ** 2);
    }

    return 0;
  }

  angleBetween(point1: Point | null, point2: Point | null) {
    if (point1 && point2) {
      return Math.atan2(point2.x - point1.x, point2.y - point1.y);
    }
    return 0;
  }

  handlePercentage(filledInPixels = 0) {
    let finishPercent = 70;
    if (this.props.finishPercent !== undefined) {
      finishPercent = this.props.finishPercent;
    }

    if (filledInPixels >= finishPercent && !this.state.finished) {
      if (this.props.fadeOutOnComplete !== false) {
        this.canvas.style.transition = '1s';
        this.canvas.style.opacity = '0';
      }

      this.setState({ finished: true });
      if (this.props.onComplete) {
        this.props.onComplete();
      }
    }
  }

  handleTriggerPercentage(filledInPixels = 0) {
    let finishPercent = 5;
    if (this.props.triggerPercent !== undefined) {
      finishPercent = this.props.triggerPercent;
    }

    if (filledInPixels === finishPercent && this.props.onTrigger) {
      this.props.onTrigger();
    }
  }

  handleMouseDown = (e: any) => {
    this.isDrawing = true;
    this.lastPoint = this.getMouse(e, this.canvas);
  };

  handleMouseMove = (e: any) => {
    if (!(this.canvas && this.ctx)) return;

    const currentPoint = this.getMouse(e, this.canvas);
    if (this.lastPoint && this.isDrawing) {
      const distance = this.distanceBetween(this.lastPoint, currentPoint);
      const angle = this.angleBetween(this.lastPoint, currentPoint);

      for (let i = 0; i < distance; i++) {
        const x = this.lastPoint.x + Math.sin(angle) * i;
        const y = this.lastPoint.y + Math.cos(angle) * i;

        this.ctx.globalCompositeOperation = 'destination-out';

        if (this.brushImage && this.props.customBrush) {
          this.ctx.drawImage(
            this.brushImage,
            x,
            y,
            this.props.customBrush.width,
            this.props.customBrush.height
          );
        } else {
          this.ctx.beginPath();
          this.ctx.arc(x, y, this.props.brushSize || 20, 0, 2 * Math.PI, false);
          this.ctx.fill();
        }
      }
      this.lastPoint = currentPoint;
    }

    // Обчислюємо завжди прогрес і тригеримо onComplete/onTrigger
    const filled = this.getFilledInPixels(32);
    if (!this.state.finished) {
      this.handlePercentage(filled);
      this.handleTriggerPercentage(filled);
    }
  };

  handleMouseUp = () => {
    this.isDrawing = false;
  };

  render() {
    const containerStyle = {
      width: this.props.width + 'px',
      height: this.props.height + 'px',
      position: 'relative' as const,
      WebkitUserSelect: 'none' as const,
      MozUserSelect: 'none' as const,
      msUserSelect: 'none' as const,
      userSelect: 'none' as const,
    };

    const canvasStyle = {
      position: 'absolute' as const,
      top: 0,
      zIndex: 1,
    };

    const resultStyle = {
      visibility: this.state.loaded
        ? ('visible' as const)
        : ('hidden' as const),
      width: '100%',
      height: '100%',
    };

    return (
      <div className="ScratchCard__Container" style={containerStyle}>
        <canvas
          className="ScratchCard__Canvas"
          height={this.props.height}
          onMouseDown={this.handleMouseDown}
          onMouseMove={this.handleMouseMove}
          onMouseUp={this.handleMouseUp}
          onTouchEnd={this.handleMouseUp}
          onTouchMove={this.handleMouseMove}
          onTouchStart={this.handleMouseDown}
          ref={(ref: any) => {
            this.canvas = ref;
          }}
          style={canvasStyle}
          width={this.props.width}
        />
        <div className="ScratchCard__Result" style={resultStyle}>
          {this.props.children}
        </div>
      </div>
    );
  }
}

export default Scratch;
